import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { NavLink, useLocation } from 'react-router-dom'
import { NavBadge } from './NavBadge'
import type { NavItem } from './nav'

/**
 * Aufklappbares Tab-Menü (z. B. „Abrechnung ▾"). Bündelt selten gebrauchte Tabs, damit die
 * Navi-Leiste nicht überläuft. Schließt bei Klick außerhalb, Escape und nach jeder Navigation;
 * die Schaltfläche erbt den Aktiv-Stil, sobald eine der Unterseiten offen ist.
 *
 * A11y: bewusst ein Disclosure-Pattern (Button mit aria-expanded + normale Links), KEIN ARIA-Menü
 * (role="menu"/"menuitem"). Ein ARIA-Menü würde Pfeiltasten-Navigation/Fokus-Management versprechen,
 * das hier nicht implementiert ist — für eine Link-Sammlung ist Disclosure korrekt.
 *
 * Das Popover wird per Portal ins <body> gerendert und fest (position: fixed) unter der Schaltfläche
 * platziert. Grund: die Tab-Leiste (.wf-tabs) scrollt horizontal und würde ein eingebettetes Menü
 * vertikal abschneiden. Die Kopfleiste ist sticky → die Button-Position bleibt beim Seiten-Scroll
 * stabil; neu vermessen wird nur bei Größenänderung des Fensters.
 */
export function NavMenu({ label, items, badge = 0 }: { label: string; items: NavItem[]; badge?: number }) {
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)
  const wrapRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const location = useLocation()
  const active = items.some(
    (item) => location.pathname === item.to || location.pathname.startsWith(`${item.to}/`),
  )

  // Nach jeder Navigation (Pfadwechsel) schließen.
  useEffect(() => {
    setOpen(false)
  }, [location.pathname])

  // Position unter der Schaltfläche bestimmen, solange das Menü offen ist (Öffnen + Fenstergröße).
  useEffect(() => {
    if (!open) return
    function place() {
      const rect = buttonRef.current?.getBoundingClientRect()
      if (rect) setPos({ top: rect.bottom, left: rect.left })
    }
    place()
    window.addEventListener('resize', place)
    return () => window.removeEventListener('resize', place)
  }, [open])

  // Klick außerhalb (Button + Popover) oder Escape schließt das Menü.
  useEffect(() => {
    if (!open) return
    function onPointerDown(event: MouseEvent) {
      const target = event.target as Node
      if (wrapRef.current?.contains(target)) return
      if (target instanceof Element && target.closest('.wf-tabmenu__pop')) return
      setOpen(false)
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  return (
    <div className="wf-tabmenu" ref={wrapRef}>
      <button
        type="button"
        ref={buttonRef}
        className={active ? 'wf-tab wf-tab--active' : 'wf-tab'}
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        {label}
        <NavBadge n={badge} />
        <span className="wf-tabmenu__caret" aria-hidden="true">
          ▾
        </span>
      </button>
      {open && pos
        ? createPortal(
            <div className="wf-tabmenu__pop" style={{ top: pos.top, left: pos.left }}>
              {items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    isActive ? 'wf-tabmenu__item wf-tabmenu__item--active' : 'wf-tabmenu__item'
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </div>,
            document.body,
          )
        : null}
    </div>
  )
}
